import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    Box, useDisclosure, useToast
} from '@chakra-ui/react';
import { useDispatch, useSelector } from "react-redux";
import {
    addMessageInDocumentGeneration,
    addToCurrentChatResponseGeneration,
    appendToMessageInDocumentGeneration,
    removeMessageInDocumentGeneration,
    resetCurrentChatResponseGeneration,
    setCurrentChatClaudeIDGeneration,
    setCurrentChatQuestionGeneration,
    setCurrentChatTypeGeneration,
    setCurrentChatUserIDGeneration,
    setIsPricingModalOpen, setIsResearchModeOn,
    updateMessageInDocumentGeneration
} from "../../redux/reducers";
import { v4 as uuidv4 } from 'uuid';
import {useAuth} from "../../redux/AuthProvider";
import {Generation} from "./Generation";
import GeneratePromptScreen1 from "../Modals/Generation/GeneratePromptScreen1";
import {getClaudeResponse, postChatPair} from "../../utils/api";
import {largeContextSynthesis, pollLargeContextProgress, postChatPairMultidoc} from "../../utils/multi-doc-helpers";
import {useTranslation} from "react-i18next";
import {createMultidocSession, multidocChat} from "../../utils/multi-doc-helpers";
import {useServerIP} from "../../redux/ServerIPContext";

// handleChatResponseNoSelection = async (type, pageNumber, sessionToken, socketID, question)
export function SidebarGeneration({ documentGeneration, sessionTokenGeneration, width, socketIDGeneration, socketGeneration, pdfRef}) {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const currentChatUserIDGeneration = useSelector(state => state.settings.currentChatUserIDGeneration);
    const currentChatTypeGeneration = useSelector(state => state.settings.currentChatTypeGeneration);
    const currentChatSelectionTextGeneration = useSelector(state => state.settings.currentChatSelectionTextGeneration);
    const currentChatQuestionGeneration = useSelector(state => state.settings.currentChatQuestionGeneration);
    const currentChatClaudeIDGeneration = useSelector(state => state.settings.currentChatClaudeIDGeneration);
    const currentChatResponseGeneration = useSelector(state => state.settings.currentChatResponseGeneration);
    const generations = useSelector(state => state.documents.savedGenerations);
    // const currentMessagesGeneration = generations.find(doc => doc.id === documentGeneration.id).messages;

    const { isTypingGeneration, setIsTypingGeneration } = useAuth();
    const {isThinkingGeneration, setIsThinkingGeneration} = useAuth();
    const toast = useToast();
    const auth = useAuth();
    const { isOpen: isOpenGeneratePromptScreen1, onOpen: onOpenGeneratePromptScreen1, onClose: onCloseGeneratePromptScreen1 } = useDisclosure();
    const currentGenerationSessionToken = useSelector(state => state.settings.currentGenerationSessionToken);
    const { serverIPs, setServerIPs, isDev } = useServerIP();
    // const [intervalHook, setIntervalHook] = useState(0)

    // Large context synthesis state
    const [isLargeContextProcessing, setIsLargeContextProcessing] = useState(false);
    const [largeContextTaskId, setLargeContextTaskId] = useState(null);
    const [largeContextProgressHistory, setLargeContextProgressHistory] = useState([]);
    const largeContextPollingRef = useRef(null);

    // Cleanup polling interval on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (largeContextPollingRef.current) {
                clearInterval(largeContextPollingRef.current);
                largeContextPollingRef.current = null;
            }
        };
    }, []);

    // Handle large context synthesis when input exceeds token limit (400 response)
    const handleLargeContextSynthesis = useCallback(async (prompt, userGenerationID, claudeGenerationID) => {
        const userID = auth.isLoggedIn ? localStorage.getItem("userID") : "11111111-1111-1111-1111-111111111111";
        const largeContextStartTime = Date.now();

        console.log("handleLargeContextSynthesis called - setting isLargeContextProcessing to true");
        console.log("[AGENT MODE TIMING] Chat submitted at:", new Date(largeContextStartTime).toISOString());
        console.log("Socket ID for streaming:", socketIDGeneration);

        // Clear any previous progress history
        setLargeContextProgressHistory([]);

        setIsLargeContextProcessing(true);
        setIsThinkingGeneration(true);

        // Show toast to inform user about large context processing
        toast({
            title: "Large document set detected",
            description: "Using our advanced agent to process your documents. This may take a bit longer.",
            status: "info",
            duration: 6000,
            isClosable: true,
            position: "top"
        });

        try {
            const response = await largeContextSynthesis(
                userID,
                documentGeneration.id,
                prompt,
                socketIDGeneration, // socketio_stream_room for streaming updates. This is just "user_{user_id}"
                serverIPs.SERVER_IP_MULTIDOC,
                toast
            );

            if (response && response.status === 200) {
                const celeryTaskId = response.celeryTaskId;
                setLargeContextTaskId(celeryTaskId);

                // Start polling for progress
                largeContextPollingRef.current = setInterval(async () => {
                    try {
                        const progressData = await pollLargeContextProgress(celeryTaskId, serverIPs.SERVER_IP_MULTIDOC);
                        console.log("Large context progress:", progressData);

                        if (progressData) {
                            // Update progress history for the UI
                            if (progressData.result?.history && Array.isArray(progressData.result.history)) {
                                console.log("[SidebarGeneration] Setting progress history:", progressData.result.history.length, "items");
                                setLargeContextProgressHistory(progressData.result.history);
                            }
                            if (progressData.state === "SUCCESS") {
                                // Task completed successfully
                                const largeContextEndTime = Date.now();
                                const totalTimeSeconds = ((largeContextEndTime - largeContextStartTime) / 1000).toFixed(2);
                                console.log("[AGENT MODE TIMING] SUCCESS at:", new Date(largeContextEndTime).toISOString());
                                console.log(`[AGENT MODE TIMING] Total time from chat submission to SUCCESS: ${totalTimeSeconds} seconds`);

                                clearInterval(largeContextPollingRef.current);
                                largeContextPollingRef.current = null;

                                // Get the presigned URL from the result
                                const result = progressData.result;
                                const presignedUrl = result?.presigned_url_of_synthesis;

                                if (presignedUrl) {
                                    try {
                                        // Fetch the actual response text from S3
                                        const textResponse = await fetch(presignedUrl);
                                        const finalResponse = await textResponse.text();

                                        setIsLargeContextProcessing(false);
                                        setIsThinkingGeneration(false);
                                        setIsTypingGeneration(false);
                                        setLargeContextTaskId(null);
                                        setLargeContextProgressHistory([]);

                                        if (finalResponse) {
                                            dispatch(updateMessageInDocumentGeneration({
                                                messageID: claudeGenerationID,
                                                type: "general_multidoc",
                                                user: 'Gemini',
                                                question: prompt,
                                                response: finalResponse,
                                                sourcesList: [],
                                                supportsList: [],
                                                documentID: documentGeneration.id
                                            }));

                                            // Save the chat pair
                                            postChatPairMultidoc([
                                                { id: userGenerationID, type: "general_multidoc", user: 'User', selection_text: null, question: prompt },
                                                { id: claudeGenerationID, type: "general_multidoc", user: 'Gemini', selection_text: null, question: prompt, response: finalResponse }
                                            ], localStorage.getItem("userID"), documentGeneration.id, auth.isLoggedIn, serverIPs.SERVER_IP_MULTIDOC);

                                            // toast({
                                            //     title: "Analysis complete!",
                                            //     description: "Your large document set has been analyzed.",
                                            //     status: "success",
                                            //     duration: 5000,
                                            //     isClosable: true,
                                            // });
                                        } else {
                                            // Empty response from S3
                                            dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                                            dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));

                                            toast({
                                                title: "Processing completed but response was empty",
                                                description: "Please try again.",
                                                status: "warning",
                                                duration: 5000,
                                                isClosable: true,
                                            });
                                        }
                                    } catch (fetchError) {
                                        console.error("Error fetching synthesis from S3:", fetchError);
                                        setIsLargeContextProcessing(false);
                                        setIsThinkingGeneration(false);
                                        setIsTypingGeneration(false);
                                        setLargeContextTaskId(null);
                                        setLargeContextProgressHistory([]);

                                        dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                                        dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));

                                        toast({
                                            title: "Error retrieving response",
                                            description: "Failed to fetch the final result. Please try again.",
                                            status: "error",
                                            duration: 5000,
                                            isClosable: true,
                                        });
                                    }
                                } else {
                                    // No presigned URL in result
                                    setIsLargeContextProcessing(false);
                                    setIsThinkingGeneration(false);
                                    setIsTypingGeneration(false);
                                    setLargeContextTaskId(null);
                                    setLargeContextProgressHistory([]);

                                    dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                                    dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));

                                    toast({
                                        title: "Processing completed but no response received",
                                        description: "Please try again.",
                                        status: "warning",
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                }
                            } else if (progressData.state === "FAILURE" || progressData.state === "REVOKED") {
                                // Task failed
                                clearInterval(largeContextPollingRef.current);
                                largeContextPollingRef.current = null;
                                setIsLargeContextProcessing(false);
                                setIsThinkingGeneration(false);
                                setIsTypingGeneration(false);
                                setLargeContextTaskId(null);
                                setLargeContextProgressHistory([]);

                                dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                                dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));

                                toast({
                                    title: "Agent mode failed",
                                    description: "An error occurred while processing your documents. Please try again",
                                    status: "error",
                                    duration: null,
                                    isClosable: true,
                                });
                            }
                            // If PENDING or PROGRESS, continue polling
                        }
                    } catch (pollError) {
                        console.error("Large context polling error:", pollError);
                        // Don't stop polling on minor errors
                    }
                }, 4000); // Poll every 4 seconds
            } else {
                // Failed to start large context synthesis
                setIsLargeContextProcessing(false);
                setIsThinkingGeneration(false);
                setLargeContextProgressHistory([]);
                dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));
            }
        } catch (error) {
            console.error("Large context synthesis error:", error);
            setIsLargeContextProcessing(false);
            setIsThinkingGeneration(false);
            setLargeContextProgressHistory([]);
            dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
            dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));

            if (largeContextPollingRef.current) {
                clearInterval(largeContextPollingRef.current);
                largeContextPollingRef.current = null;
            }
        }
    }, [auth.isLoggedIn, documentGeneration.id, socketIDGeneration, serverIPs.SERVER_IP_MULTIDOC, toast, dispatch, setIsThinkingGeneration, setIsTypingGeneration]);

    // // type, message_responding_to, follow_up_question, sessionToken, socketID
    // const handleNewMessageGeneration = (chatInputGeneration) => {
    //     handleChatResponseNoSelection('general_question', null, sessionTokenGeneration, socketIDGeneration, chatInputGeneration);
    //     dispatch(setCurrentChatInputGeneration(''));
    // };

    // finish adding in the true and false for isThinking and isTyping later

    const getMessageHistory = () => {
        let message_history = []
        const message_history_length = documentGeneration.messages.length;

        // If message_history_length is 0, leave message_history as an empty array
        if (message_history_length > 0) {
            // Determine how many messages to include in the history
            let messageLimit;
            if (message_history_length === 2) {
                messageLimit = 2; // Last 2 messages for message_history_length of 2
            } else if (message_history_length === 4) {
                messageLimit = 4; // Last 4 messages for message_history_length of 4
            } else if (message_history_length > 4) {
                messageLimit = 4; // Most recent 4 messages for message_history_length greater than 4
            } else {
                messageLimit = message_history_length; // This handles any case less than 2, though not explicitly required by the spec
            }

            // Iterate over the messages to populate message_history accordingly
            for (let i = message_history_length - 1; i >= message_history_length - messageLimit; i--) {
                const message = documentGeneration.messages[i];
                if (message.user === 'User') {
                    message_history.push({
                        user: "User",
                        response: message.question || '',
                        type: 'general_multidoc',
                        id: message.id,
                        selection_text: null,
                    });
                } else {
                    message_history.push({
                        user: "Assistant",
                        response: message.response || '',
                        id: message.id,
                    });
                }
            }
            console.log("MESSAGE LIMIT", messageLimit);
        }
        console.log("MESSAGE HISTORY LENGTH", message_history.length)
        console.log("MESSAGE HISTORY", message_history);
        return message_history;
    }

    const startGeneration = async (prompt, sliderValue, shouldReferenceSources, isNativeChat = false,
                                   citationFormat = "default", websearch = false, forceLargeContext = false) => {
        const { userGenerationID, claudeGenerationID } = handleMessageCreation("general_multidoc", prompt, sliderValue, shouldReferenceSources);
        const userID = auth.isLoggedIn ? localStorage.getItem("userID") : "11111111-1111-1111-1111-111111111111";

        dispatch(resetCurrentChatResponseGeneration());
        dispatch(setCurrentChatUserIDGeneration(userGenerationID));
        dispatch(setCurrentChatClaudeIDGeneration(claudeGenerationID));
        dispatch(setCurrentChatQuestionGeneration(prompt));
        dispatch(setCurrentChatTypeGeneration("general_multidoc"));
        setIsThinkingGeneration(true);
        setIsTypingGeneration(false);
        // let sessionToken = sessionTokenGeneration;
        // console.log("SESSION TOKEN RIGHT BEFORE CHAT", currentGenerationSessionToken)
        // if (currentGenerationSessionToken === "") {
        //     const res = await createMultidocSession(documentGeneration.id, userID);
        //     let sessionToken = res.multidocSessionToken;
        //     dispatch(setCurrentGenerationSessionToken(sessionToken));
        // }

        // If forceLargeContext is enabled, directly use the large context synthesis agent
        if (forceLargeContext) {
            console.log("Force large context mode enabled, using large context synthesis agent");
            handleLargeContextSynthesis(prompt, userGenerationID, claudeGenerationID);
            return;
        }

        //export const multidocChat = async (user_id, generation_session_token, prompt, word_count, should_reference_sources,
        //                                       socketID, toast) => {
        const messageHistory = getMessageHistory();
        // everything in this if statement is exactly what it was before

        console.log("MESSAGE HISTORY SENT FOR MULTIDOC:", messageHistory);
        multidocChat(userID, sessionTokenGeneration, prompt, socketIDGeneration, toast, messageHistory, serverIPs.SERVER_IP_MULTIDOC, citationFormat, websearch).then(response => {
            if (response !== null) {
                if (response.status === 402) {
                    console.log("pricing_modal_shown")
                    socketGeneration.off('receive_message');
                    console.log("Claude response PAID PLAN:", response.response)
                    localStorage.setItem("isChatExceeded", "true");
                    dispatch(setIsPricingModalOpen(true));
                    setIsTypingGeneration(false);
                    setIsThinkingGeneration(false);
                    dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                    dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));
                }
                else if (response.status === 400) {
                    // Token limit exceeded - use large context synthesis agent
                    socketGeneration.off('receive_message');
                    console.log("Token limit exceeded, switching to large context synthesis agent");

                    // Call the large context synthesis agent instead
                    handleLargeContextSynthesis(prompt, userGenerationID, claudeGenerationID);
                }
                else {
                    if (isTypingGeneration || !isTypingGeneration) {
                        setIsTypingGeneration(false);
                        setIsThinkingGeneration(false);
                        socketGeneration.off('receive_message');
                        if (response.response !== "") {
                            // console.log("MULTIDOC Chat response:", response)
                            // console.log("updateMessage payload", {
                            //     messageID: claudeGenerationID, type: "general_multidoc", user: 'Gemini', question: prompt,
                            //     response: response.response, documentID: documentGeneration.id
                            // })
                            dispatch(updateMessageInDocumentGeneration({ messageID: claudeGenerationID, type: "general_multidoc",
                                user: 'Gemini', question: prompt, response: response.response,
                                sourcesList: response.sourcesList, supportsList: response.supportsList,
                                documentID: documentGeneration.id }));

                            // console.log("Claude response SESSION EXPIRED:", responseText) // "Claude response SESSION EXPIRED:" "undefined"
                            postChatPairMultidoc([
                                { id: userGenerationID, type: "general_multidoc", user: 'User', selection_text: null, question: prompt },
                                { id: claudeGenerationID, type: "general_multidoc", user: 'Gemini', selection_text: null, question: prompt, response: response.response }
                            ], localStorage.getItem("userID"), documentGeneration.id, auth.isLoggedIn, serverIPs.SERVER_IP_MULTIDOC);
                        }
                    }
                }
            } else {
                // Response is NULL
                socketGeneration.off('receive_message');
                setIsTypingGeneration(false);
                setIsThinkingGeneration(false);
                dispatch(removeMessageInDocumentGeneration({ messageID: userGenerationID, documentID: documentGeneration.id }));
                dispatch(removeMessageInDocumentGeneration({ messageID: claudeGenerationID, documentID: documentGeneration.id }));
            }

        }).catch(error => {
            socketGeneration.off('receive_message');
            console.error("Error getting Claude response:", error);
        });


        socketGeneration.on('receive_message', function(data) {
            // console.log("MULTIDOC message from Claude:", data.message)
            setIsThinkingGeneration(false);
            setIsTypingGeneration(true);
            addToCurrentChatResponseGeneration(data.message);
            dispatch(appendToMessageInDocumentGeneration({ messageID: claudeGenerationID, type: "general_multidoc", user: 'Gemini',
                selection_text: "", question: prompt, response: data.message, documentID: documentGeneration.id }));
        });
    };

    // const checkGenerationProgress = async (userID, sessionToken, claudeGenerationID, celeryTaskID) => {
    //     const interval = setInterval(async () => {
    //         const response = await axios.post(SERVER_IP + '/pdf/generation_progress', {
    //             "user_id": userID.toString(),
    //             "generation_session_token": sessionToken.toString(),
    //             "task_id": celeryTaskID.toString(),
    //         });
    //         console.log("response from /pdf/generation_progress", response);
    //         if (response.data.status === 200) {
    //             console.log("RETURNED CONTENT", response.data.message.generation_content);  // IMPORTANT LINE
    //             const responseText = response.data.message.generation_content;
    //
    //             // setIsTypingGeneration(false);
    //             // setIsThinkingGeneration(false);
    //             // socketGeneration.off('receive_message');
    //             // dispatch(updateMessageInDocumentGeneration({ messageID: claudeGenerationID, response: responseText,
    //             //     documentID: documentGeneration.id }));
    //             clearInterval(interval);
    //             // else {
    //             //     if (!responseText === undefined) {
    //             //         dispatch(updateMessageInDocumentGeneration({ messageID: claudeGenerationID, response: "",
    //             //             documentID: documentGeneration.id }));
    //             //     }
    //             // }
    //         } else if (response.data.status === 205) {
    //             console.log("still processing");
    //         } else if (response.data.status === 401) {
    //             toast({
    //                 title: t("Oops, something went wrong. Please try again."),
    //                 status: "error",
    //                 duration: null,
    //                 isClosable: true,
    //             });
    //             clearInterval(interval);
    //             setIsTypingGeneration(false);
    //             setIsThinkingGeneration(false);
    //             socketGeneration.off('receive_message');
    //         }
    //     }, 4000);
    //     setIntervalHook(interval);
    // };

    const handleMessageCreation = (type, question, wordCount, shouldReferenceSources) => {
        const userIDHere = uuidv4();
        const claudeID = uuidv4();
        socketGeneration.off('receive_message');
        const userMessage = {
            id: userIDHere,
            type: type,
            user: 'User',
            question: question,
            // word_count: wordCount,
            // should_reference_sources: shouldReferenceSources,
        };
        const claudeMessage = {
            id: claudeID,
            type: type,
            user: 'Gemini',
            question: question,
            // word_count: wordCount,
            // should_reference_sources: shouldReferenceSources,
            response: ""
        };
        dispatch(addMessageInDocumentGeneration({ messageToAdd: userMessage, documentID: documentGeneration.id }));
        dispatch(addMessageInDocumentGeneration({ messageToAdd: claudeMessage, documentID: documentGeneration.id }));
        return { userGenerationID: userIDHere, claudeGenerationID: claudeID };
    }

    const stopClaudeResponse = () => {
        setIsThinkingGeneration(false);
        setIsTypingGeneration(false);
        socketGeneration.off('receive_message');
        postChatPair([
            {
                id: currentChatUserIDGeneration, type: currentChatTypeGeneration, user: 'User',
                selection_text: currentChatSelectionTextGeneration, question: currentChatQuestionGeneration
            },
            {
                id: currentChatClaudeIDGeneration, type: currentChatTypeGeneration, user: 'Gemini',
                selection_text: currentChatSelectionTextGeneration, question: currentChatQuestionGeneration,
                response: currentChatResponseGeneration
            }
        ], localStorage.getItem("userID"),  documentGeneration.id, auth.isLoggedIn, serverIPs.SERVER_IP).then(r =>{
            dispatch(resetCurrentChatResponseGeneration())
        });
    }

    return (
        <Box width={`${width}%`} height="100%" display="flex" flexDirection="column">
            <Generation theDocumentGeneration={documentGeneration}
                        sessionTokenGeneration={sessionTokenGeneration} socketIDGeneration={socketIDGeneration} socketGeneration={socketGeneration}
                        stopClaudeResponseGeneration={stopClaudeResponse} startGeneration={startGeneration} pdfRef={pdfRef}
                        isLargeContextProcessing={isLargeContextProcessing} largeContextTaskId={largeContextTaskId}
                        largeContextProgressHistory={largeContextProgressHistory}/>
            <GeneratePromptScreen1 isOpenGeneratePromptScreen1={isOpenGeneratePromptScreen1} onCloseGeneratePromptScreen1={onCloseGeneratePromptScreen1}/>
        </Box>
    );
}
